/**
 * Why a send was withheld — the `sent_emails.skipReason` codes and the
 * `emailSkipReason` locale key each one renders as.
 *
 * MIRROR of main-consumer/src/lib/email-sent-repo.ts `EmailSkipReason`. The
 * consumer is the only writer; this map is the only place that turns a code
 * into words, and three surfaces read it (the sent-mails table chip, the
 * guest-drawer badge, the detail dialog's "not sent" block). A code the
 * consumer adds and this map does not know still renders — as the generic
 * `unknown` line carrying the raw code — and
 * e2e_main/scripts/check-email-skip-reason-drift.sh goes red on the gap.
 *
 * Static on purpose: `t.has()` would decide the same thing at runtime, but
 * every vitest mock of next-intl in this repo returns a bare function, and a
 * hook that touches `.has` would fail in exactly the tests meant to pin it.
 */
export const EMAIL_SKIP_REASON_KEYS = {
  /** `guest.emailOptIn === false` on a marketing-gated send. */
  "opt-out": "optOut",
  /** The guest had no email address at send time. */
  "no-email": "noEmail",
  /** SES reported a permanent bounce / complaint for this guest's address. */
  undeliverable: "undeliverable",
  /** The guest document was gone by the time the send ran. */
  "guest-missing": "guestMissing",
  /** The email template was gone by the time the send ran. */
  "template-missing": "templateMissing",
  /** The ADDRESS is on the platform-wide do-not-mail list, any tenant. */
  suppressed: "suppressed",
  /** The tenant's bounce / complaint rate tripped the platform brake. */
  "sender-blocked": "senderBlocked",
  /**
   * The address filed a SPAM COMPLAINT (suppression hit with reason
   * COMPLAINT). Skipped on every send class — an organisational notice, which
   * passes the opt-out gate, must never reach a complainant either.
   */
  complaint: "complaint",
  /**
   * Welle Z 1f: the company's remaining mail quota did not cover the send
   * (consumer `QUOTA_SHORTFALL_BLOCK=enforce`). No writer before 21.10. —
   * mirrored in the 15.10. bundle so the cockpit names it before the first
   * row can exist.
   */
  "quota-exhausted": "quotaExhausted",
  /**
   * Plan "Saubere Eingaben" P0c: the consumer's gate before SES — the stored
   * address does not pass the one rule (`email-rule`: ASCII before the "@",
   * LDH domain, 6–254), so the send is skipped instead of failing at SES
   * with no reason (`400 Local address contains control or whitespace`,
   * 2026-10-05). The consumer never rewrites the recipient; it skips.
   */
  "invalid-email": "invalidEmail",
  /**
   * W4 Hop B (decision 11 b, contract-b §6): a mail to a third party — the
   * arrival notice to a notification contact, or the confirm mail to a new
   * one — whose event has no valid organiser privacy URL (website, then
   * portal). Such a mail names the organiser as controller and must link the
   * organiser's notice, so it is not sent at all.
   */
  "organiser-privacy-url-missing": "organiserPrivacyUrlMissing",
  /**
   * W4 Hop B N4 (contract-b §10.2): the confirm mail to a new notification
   * contact while the consumer's kill switch `ARRIVAL_NOTICE_DISABLED` is on.
   */
  "arrival-notice-disabled": "arrivalNoticeDisabled",
} as const;

export type EmailSkipReasonCode = keyof typeof EMAIL_SKIP_REASON_KEYS;
export type EmailSkipReasonKey =
  (typeof EMAIL_SKIP_REASON_KEYS)[EmailSkipReasonCode];

/** Every code this build knows, for tests and the drift gate. */
export const EMAIL_SKIP_REASON_CODES = Object.keys(
  EMAIL_SKIP_REASON_KEYS,
) as EmailSkipReasonCode[];

/**
 * The locale key for a stored code, or null for one this build does not
 * know. Own-property check, so `toString` and friends are not codes.
 */
export function emailSkipReasonKey(code: string): EmailSkipReasonKey | null {
  return Object.prototype.hasOwnProperty.call(EMAIL_SKIP_REASON_KEYS, code)
    ? EMAIL_SKIP_REASON_KEYS[code as EmailSkipReasonCode]
    : null;
}
